import { NgFor, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    NgZone,
    OnDestroy,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SaiQuickTourFormCoachStep } from 'app/ui/sai-quick-tour/sai-quick-tour-form-coach.types';
import {
    computeSaiQuickTourCardPosition,
    estimateSaiQuickTourCardSize,
    findBestTourTargetElement,
    isSaiQuickTourMobileViewport,
    isSaiQuickTourNavTarget,
    resolveTourTargetScrollBlock,
    scrollTourTargetIntoView,
} from 'app/ui/sai-quick-tour/sai-quick-tour-layout.util';
import { SaiQuickTourService } from 'app/ui/sai-quick-tour/sai-quick-tour.service';
import { SaiQuickTourStep, SaiQuickTourTargetRect } from 'app/ui/sai-quick-tour/sai-quick-tour.types';

interface SpotlightRect {
    x: number;
    y: number;
    width: number;
    height: number;
    rx: number;
}

interface CardPosition {
    top: number;
    left: number;
}

@Component({
    selector: 'sai-quick-tour',
    templateUrl: './sai-quick-tour.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, NgFor, MatButtonModule, MatIconModule, TranslocoModule],
})
export class SaiQuickTourComponent implements OnDestroy {
    active = false;
    isOpening = false;
    stepIndex = 0;
    currentStep: SaiQuickTourStep | null = null;
    totalSteps = 0;
    stepDots: number[] = [];
    /** Last known spotlight rect — kept between steps to avoid flash. */
    pinnedSpotlight: SpotlightRect | null = null;
    showSpotlight = false;
    showFullScrim = true;
    cardPosition: CardPosition = { top: 0, left: 0 };
    isMobileDocked = false;
    isMobileAnchored = false;
    cardMaxHeight: number | null = null;
    cardWidthOverride: number | null = null;
    isCentered = true;
    contentVisible = true;
    /** Whole tip card — hidden during fade-out → route/tab → fade-in. */
    cardVisible = true;
    dir: 'ltr' | 'rtl' = 'ltr';
    primaryLabelKey = 'Next';
    /** True while a MatDialog is open — tour dim sits behind unless form-coach is active. */
    dialogOpen = false;
    /** In-dialog field coach (e.g. Add Data Source) rendered as the floating overlay tip. */
    formCoachActive = false;
    formCoachIndex = 0;
    formCoachTotal = 0;
    formCoachStep: SaiQuickTourFormCoachStep | null = null;
    formCoachDots: number[] = [];
    formCoachSampleKey: string | null = null;

    private readonly _tourService = inject(SaiQuickTourService);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _translocoService = inject(TranslocoService);
    private readonly _ngZone = inject(NgZone);
    private readonly _matDialog = inject(MatDialog);

    @ViewChild('tourCard') private _tourCardRef?: ElementRef<HTMLElement>;

    private _layoutFrame: number | null = null;
    private _layoutSettleFrame: number | null = null;
    private _refreshDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private _openingTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        this._tourService.visibleSteps$
            .pipe(takeUntilDestroyed())
            .subscribe((steps: readonly SaiQuickTourStep[]) => {
                this.totalSteps = steps.length;
                this.stepDots = Array.from({ length: this.totalSteps }, (_, i) => i);
                this._changeDetectorRef.markForCheck();
            });

        this._matDialog.afterOpened
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this._syncDialogOpenClass();
                this._changeDetectorRef.markForCheck();
            });

        this._matDialog.afterAllClosed
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this._syncDialogOpenClass();
                // Remeasure after Cancel / Add — tip was hidden or left at form-coach coords.
                if (this.active && !this._tourService.formCoach.active) {
                    this._restoreMainStepPresentation();
                    this.cardVisible = true;
                    this.contentVisible = true;
                }
                this._changeDetectorRef.markForCheck();
            });

        this._tourService.cardVisible$
            .pipe(takeUntilDestroyed())
            .subscribe((visible: boolean) => {
                this.cardVisible = visible;
                this.contentVisible = visible;
                this._changeDetectorRef.markForCheck();
            });

        this._tourService.formCoach$
            .pipe(takeUntilDestroyed())
            .subscribe((state) => {
                this.formCoachActive = state.active;
                this.formCoachIndex = state.index;
                this.formCoachTotal = state.steps.length;
                this.formCoachStep = state.active ? (state.steps[state.index] ?? null) : null;
                this.formCoachSampleKey = this.formCoachStep?.sampleKey ?? null;
                this.formCoachDots = Array.from({ length: state.steps.length }, (_, i) => i);
                this._syncDialogOpenClass();

                if (state.active) {
                    this.cardVisible = true;
                    this.contentVisible = true;
                    this.isCentered = !this.formCoachStep?.fieldTourId;
                    this._measureFormCoachTarget();
                } else if (this._matDialog.openDialogs.length > 0) {
                    // Got it / Skip guide while the Add dialog is still open: do not put the
                    // main "Click Add …" tip back under the dialog (looks stuck).
                    this._hideTipUntilDialogCloses();
                } else {
                    this._restoreMainStepPresentation();
                }
                this._changeDetectorRef.markForCheck();
            });

        this._tourService.active$
            .pipe(takeUntilDestroyed())
            .subscribe((active: boolean) => {
                this.active = active;
                if (active) {
                    this.isOpening = true;
                    this._syncStepFromService();
                    if (this._openingTimer) {
                        clearTimeout(this._openingTimer);
                    }
                    this._openingTimer = setTimeout(() => {
                        this.isOpening = false;
                        this._changeDetectorRef.markForCheck();
                    }, 280);
                } else {
                    this.isOpening = false;
                    this.pinnedSpotlight = null;
                    this.showSpotlight = false;
                    this.showFullScrim = true;
                    this.formCoachActive = false;
                    this.isMobileDocked = false;
                    this.isMobileAnchored = false;
                    this.cardMaxHeight = null;
                    this.cardWidthOverride = null;
                    document.body.classList.remove('sai-tour-dialog-open');
                    document.body.classList.remove('sai-tour-form-coach-open');
                }
                this._changeDetectorRef.markForCheck();
            });

        this._tourService.stepIndex$
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                if (this.formCoachActive) {
                    return;
                }
                this._syncStepFromService();
                this._scheduleLayout();
                this._changeDetectorRef.markForCheck();
            });

        this._tourService.targetRect$
            .pipe(takeUntilDestroyed())
            .subscribe((rect: SaiQuickTourTargetRect | null) => {
                if (this.formCoachActive) {
                    return;
                }
                const step = this._tourService.currentStep;
                const centered = !!step?.centered || !step?.target;

                if (rect) {
                    this.pinnedSpotlight = this._toSpotlight(rect);
                } else {
                    this.pinnedSpotlight = null;
                }

                this._syncOverlayVisibility();
                this._scheduleLayout();
                this._changeDetectorRef.markForCheck();
            });

        this._translocoService.langChanges$
            .pipe(takeUntilDestroyed())
            .subscribe((lang: string) => {
                this.dir = lang === 'ar' ? 'rtl' : 'ltr';
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        document.body.classList.remove('sai-tour-dialog-open');
        document.body.classList.remove('sai-tour-form-coach-open');
        if (this._layoutFrame !== null) {
            cancelAnimationFrame(this._layoutFrame);
        }
        if (this._refreshDebounceTimer) {
            clearTimeout(this._refreshDebounceTimer);
        }
        if (this._openingTimer) {
            clearTimeout(this._openingTimer);
        }
        if (this._layoutSettleFrame !== null) {
            cancelAnimationFrame(this._layoutSettleFrame);
        }
    }

    get stepNumber(): number {
        return this.formCoachActive ? this.formCoachIndex + 1 : this.stepIndex + 1;
    }

    get displayTotal(): number {
        return this.formCoachActive ? this.formCoachTotal : this.totalSteps;
    }

    get titleKey(): string {
        return this.formCoachActive
            ? (this.formCoachStep?.titleKey ?? '')
            : (this.currentStep?.titleKey ?? '');
    }

    get bodyKey(): string {
        return this.formCoachActive
            ? (this.formCoachStep?.bodyKey ?? '')
            : (this.currentStep?.bodyKey ?? '');
    }

    /** Resolves step body; complete step joins only the modules present in this run. */
    get bodyText(): string {
        if (this.formCoachActive) {
            return this._translocoService.translate(this.formCoachStep?.bodyKey ?? '');
        }
        const step = this.currentStep;
        if (step?.id === 'complete') {
            const segmentKeys = step.bodyParams?.['flowSegmentKeys'];
            if (Array.isArray(segmentKeys) && segmentKeys.length > 0) {
                const separator = this.dir === 'rtl' ? ' ← ' : ' → ';
                const flow = segmentKeys
                    .map((key) => this._translocoService.translate(String(key)))
                    .join(separator);
                return this._translocoService.translate('Quick tour — complete body', { flow });
            }
        }
        return this._translocoService.translate(this.bodyKey);
    }

    get eyebrowLabel(): string {
        return this.formCoachActive ? 'Form guide' : 'SAI Quick Tour';
    }

    get dots(): number[] {
        return this.formCoachActive ? this.formCoachDots : this.stepDots;
    }

    get activeDotIndex(): number {
        return this.formCoachActive ? this.formCoachIndex : this.stepIndex;
    }

    /** Soften main tip behind dialogs only when we are NOT coaching fields on top. */
    get behindOverlay(): boolean {
        return this.dialogOpen && !this.formCoachActive;
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        if (this.formCoachActive) {
            this._measureFormCoachTarget();
            return;
        }
        this._debouncedRefreshTarget();
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        if (this.formCoachActive) {
            this._measureFormCoachTarget();
        }
    }

    back(): void {
        if (this.formCoachActive) {
            this._tourService.backFormCoach();
            return;
        }
        this._tourService.back();
    }

    next(): void {
        if (this.formCoachActive) {
            this._tourService.nextFormCoach();
            return;
        }
        this._tourService.next();
    }

    skip(): void {
        if (this.formCoachActive) {
            this._tourService.endFormCoach();
            return;
        }
        this._tourService.skip();
    }

    /** X / Esc: leave the tour without finishing it — the next run picks up on this step. */
    close(): void {
        if (this.formCoachActive) {
            this._tourService.endFormCoach();
            return;
        }
        this._tourService.close();
    }

    primaryAction(): void {
        if (this.formCoachActive) {
            this._tourService.nextFormCoach();
            return;
        }
        if (this.stepIndex >= this.totalSteps - 1) {
            this._tourService.finish();
            return;
        }
        this._tourService.next();
    }

    onEscape(event: KeyboardEvent): void {
        event.preventDefault();
        this.close();
    }

    trackByIndex(index: number): number {
        return index;
    }

    private _syncDialogOpenClass(): void {
        this.dialogOpen = this._matDialog.openDialogs.length > 0;
        document.body.classList.toggle('sai-tour-dialog-open', this.dialogOpen && !this.formCoachActive);
        document.body.classList.toggle('sai-tour-form-coach-open', this.formCoachActive);
    }

    private _hideTipUntilDialogCloses(): void {
        this.cardVisible = false;
        this.contentVisible = false;
        this.showSpotlight = false;
        this.showFullScrim = false;
        this.pinnedSpotlight = null;
    }

    private _syncStepFromService(): void {
        this.stepIndex = this._tourService.currentStepIndex;
        this.currentStep = this._tourService.currentStep;
        this.isCentered = !!this.currentStep?.centered || !this.currentStep?.target;
        if (this.isCentered) {
            this.pinnedSpotlight = null;
            this.cardWidthOverride = null;
        }
        this.primaryLabelKey =
            this.stepIndex >= this.totalSteps - 1 ? 'Finish tour' : 'Next';
        this._syncOverlayVisibility();
    }

    private _restoreMainStepPresentation(): void {
        this._syncStepFromService();
        this._tourService.refreshTargetRect({ scroll: true });
        this._scheduleLayout();
    }

    private _measureFormCoachTarget(): void {
        const fieldId = this.formCoachStep?.fieldTourId;
        if (!fieldId) {
            this.pinnedSpotlight = null;
            this.showSpotlight = false;
            this.showFullScrim = true;
            this.isCentered = true;
            this._scheduleLayout();
            this._changeDetectorRef.markForCheck();
            return;
        }

        const el = document.querySelector(`[data-sai-tour="${fieldId}"]`) as HTMLElement | null;
        if (!el) {
            this.pinnedSpotlight = null;
            this.showSpotlight = false;
            this.showFullScrim = true;
            this._scheduleLayout();
            this._changeDetectorRef.markForCheck();
            return;
        }

        el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        const rect = el.getBoundingClientRect();
        if (rect.width < 2 || rect.height < 2) {
            this.pinnedSpotlight = null;
            this.showSpotlight = false;
            this.showFullScrim = true;
            this._scheduleLayout();
            this._changeDetectorRef.markForCheck();
            return;
        }

        this.pinnedSpotlight = this._toSpotlight({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        });
        this.showSpotlight = true;
        this.showFullScrim = false;
        this.isCentered = false;
        this._scheduleLayout();
        this._changeDetectorRef.markForCheck();
    }

    /** Full scrim until a spotlight rect exists; crossfade avoids a flash between steps. */
    private _syncOverlayVisibility(): void {
        this.showSpotlight = !!this.pinnedSpotlight;
        this.showFullScrim = !this.pinnedSpotlight;
    }

    private _debouncedRefreshTarget(): void {
        if (!this.active) {
            return;
        }
        if (this._refreshDebounceTimer) {
            clearTimeout(this._refreshDebounceTimer);
        }
        this._refreshDebounceTimer = setTimeout(() => {
            this._tourService.refreshTargetRect();
        }, 100);
    }

    private _scheduleLayout(): void {
        if (this._layoutFrame !== null) {
            cancelAnimationFrame(this._layoutFrame);
        }
        if (this._layoutSettleFrame !== null) {
            cancelAnimationFrame(this._layoutSettleFrame);
        }
        this._ngZone.runOutsideAngular(() => {
            this._layoutFrame = requestAnimationFrame(() => {
                this._layoutFrame = null;
                this._applyCardLayout();
                this._layoutSettleFrame = requestAnimationFrame(() => {
                    this._layoutSettleFrame = null;
                    this._applyCardLayout(true);
                });
            });
        });
    }

    private _applyCardLayout(settlePass = false): void {
        const layout = this._computeCardLayout();
        const positionChanged =
            this.cardPosition.top !== layout.top ||
            this.cardPosition.left !== layout.left ||
            this.isMobileDocked !== layout.mobileDocked ||
            this.isMobileAnchored !== layout.mobileAnchored ||
            this.cardMaxHeight !== layout.maxHeight ||
            this.cardWidthOverride !== layout.cardWidth;

        this.cardPosition = { top: layout.top, left: layout.left };
        this.isMobileDocked = layout.mobileDocked;
        this.isMobileAnchored = layout.mobileAnchored;
        this.cardMaxHeight = layout.maxHeight;
        this.cardWidthOverride = layout.cardWidth;

        if (this.isMobileDocked && !this.isMobileAnchored && this.pinnedSpotlight) {
            this._ensureSpotlightVisibleAboveDock(layout.height);
        }

        if (positionChanged || settlePass) {
            this._ngZone.run(() => this._changeDetectorRef.markForCheck());
        }
    }

    private _computeCardLayout(): CardPosition & {
        mobileDocked: boolean;
        mobileAnchored: boolean;
        maxHeight: number | null;
        cardWidth: number | null;
        height: number;
    } {
        const navTarget = isSaiQuickTourNavTarget(this.currentStep?.target);
        const mobileWithSpotlight =
            isSaiQuickTourMobileViewport(window.innerWidth) && !!this.pinnedSpotlight;
        const cardSize = mobileWithSpotlight
            ? estimateSaiQuickTourCardSize(window.innerWidth)
            : this._measureCardSize();
        const placement = this.formCoachActive
            ? 'bottom'
            : (this.currentStep?.placement ?? 'bottom');
        const layout = computeSaiQuickTourCardPosition({
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            spotlight: this.pinnedSpotlight,
            placement,
            cardSize,
            navTarget,
        });

        return {
            top: layout.top,
            left: layout.left,
            mobileDocked: layout.mobileDocked,
            mobileAnchored: !!layout.mobileAnchored,
            maxHeight: layout.maxHeight ?? null,
            cardWidth: layout.cardWidth ?? null,
            height: cardSize.height,
        };
    }

    private _measureCardSize(): { width: number; height: number } {
        const measured = this._tourCardRef?.nativeElement.getBoundingClientRect();
        if (measured && measured.width > 0 && measured.height > 0) {
            return { width: measured.width, height: measured.height };
        }
        return estimateSaiQuickTourCardSize(window.innerWidth);
    }

    /** Keep the spotlight above a bottom-docked tip card on phones. */
    private _ensureSpotlightVisibleAboveDock(cardHeight: number): void {
        const step = this.formCoachActive
            ? this.formCoachStep?.fieldTourId
            : this.currentStep?.target;
        if (!step) {
            return;
        }

        const selector = this.formCoachActive
            ? `[data-sai-tour="${step}"]`
            : step;
        const el = this.formCoachActive
            ? document.querySelector(selector)
            : findBestTourTargetElement(selector);
        if (!el) {
            return;
        }

        const margin = 16;
        const reservedBottom = cardHeight + margin * 2;
        const rect = el.getBoundingClientRect();
        const spotlightBottom = rect.bottom + 6;
        const maxVisibleBottom = window.innerHeight - reservedBottom;

        if (spotlightBottom > maxVisibleBottom) {
            const isMobile = isSaiQuickTourMobileViewport(window.innerWidth);
            const targetSelector = this.formCoachActive
                ? `[data-sai-tour="${step}"]`
                : (step as string);
            if (isMobile) {
                scrollTourTargetIntoView(el, true, targetSelector);
            } else {
                el.scrollIntoView({
                    block: resolveTourTargetScrollBlock(false, targetSelector),
                    inline: 'nearest',
                    behavior: 'auto',
                });
            }
            if (this.formCoachActive) {
                this._measureFormCoachTarget();
            } else {
                this._tourService.refreshTargetRect();
            }
        }
    }

    private _toSpotlight(rect: SaiQuickTourTargetRect): SpotlightRect {
        const pad = 6;
        return {
            x: Math.max(0, rect.left - pad),
            y: Math.max(0, rect.top - pad),
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            rx: 12,
        };
    }
}
